import { useState, useEffect } from 'react';
import { Share2, Link2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const SITE_URL = 'https://ratnatrraya.consulting';

export default function FloatingShare({ path, title, description }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const fullUrl = `${SITE_URL}${path}`;

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest('[data-share-menu]')) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const shareWhatsApp = () => {
    const msg = `${title}\n\n${description}\n\n${fullUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`, '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-share-menu data-testid="floating-share">
      {open && (
        <div className="absolute bottom-16 right-0 bg-white rounded-xl shadow-xl border border-slate-200 p-2 w-56 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="share-menu">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold px-3 pt-1 pb-2">Share this page</p>

          <button
            onClick={copyLink}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="share-copy-link"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4 text-slate-500" />}
            <span className="text-sm text-slate-700">{copied ? 'Copied!' : 'Copy link'}</span>
          </button>

          <button
            onClick={shareWhatsApp}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-green-50 transition-colors text-left"
            data-testid="share-whatsapp"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            <span className="text-sm text-slate-700">WhatsApp</span>
          </button>

          <button
            onClick={shareLinkedIn}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-colors text-left"
            data-testid="share-linkedin"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#0A66C2]"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            <span className="text-sm text-slate-700">LinkedIn</span>
          </button>
        </div>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`h-12 w-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
          open
            ? 'bg-slate-700 hover:bg-slate-800 rotate-45'
            : 'bg-[#1e3a5f] hover:bg-[#152d4a] hover:scale-105'
        }`}
        data-testid="floating-share-btn"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <Share2 className="w-5 h-5 text-white" />}
      </button>
    </div>
  );
}
